import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarDays, Baby, Music, BookOpen, Users, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import { CHURCH_LOCAL_LIST } from "@/data/churchesLocal";
import { DIRECTIONS_URL, AREA_DESCRIPTOR, formatAddress, useSettings } from "@/hooks/SettingsContext";
import { trackPlanVisitClick, trackDirectionsClick } from "@/lib/analytics";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const BASE = "https://vcclaychurch.com";

const GLANCE = [
  { icon: CalendarDays, label: "Launching", value: "January 2027 - Service #1" },
  { icon: Clock, label: "After Launch", value: "Sundays 11:00 AM - Wednesdays 7:00 PM" },
  { icon: MapPin, label: "Address", value: "3180 CR 220 STE 6-7, Middleburg, FL 32068" },
  { icon: Baby, label: "Kids", value: "VC Kids nursery & children's church" },
];

export default function ChurchLocalPage({ data }) {
  const { settings } = useSettings();
  if (!data) return null;
  const s = settings || {};
  const path = `/${data.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Church",
        name: "Victory Chapel Clay",
        alternateName: ["Victory Chapel Christian Church", "VC Clay"],
        description: `A new Christian church serving ${data.city} and greater Clay County, Florida. Launching January 2027.`,
        url: `${BASE}${path}`,
        address: {
          "@type": "PostalAddress",
          streetAddress: "3180 CR 220 STE 6-7",
          addressLocality: "Middleburg",
          addressRegion: "FL",
          postalCode: "32068",
          addressCountry: "US",
        },
        areaServed: [data.city, "Clay County"],
        telephone: "+1-904-383-2559",
        email: "Victorychapelclay@gmail.com",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "Churches in Clay County", item: `${BASE}/churches-in-clay-county-fl` },
          { "@type": "ListItem", position: 3, name: `Church ${data.prep} ${data.city}, FL` },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: data.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  };

  return (
    <>
      <SEO title={data.metaTitle} description={data.description} path={path} jsonLd={jsonLd} image="/you-belong-here.jpg" />

      <section className="grain relative overflow-hidden bg-navy py-16 text-white sm:py-24">
        <img src="/you-belong-here.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" loading="eager" />
        <div className="absolute inset-0 bg-navy/75" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-white/60" data-testid="breadcrumb">
            <Link to="/" className="transition-colors hover:text-white">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <Link to="/churches-in-clay-county-fl" className="transition-colors hover:text-white">Churches in Clay County</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page" className="text-white/85">Church {data.prep} {data.city}, FL</span>
          </nav>
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Church {data.prep} {data.city}, FL</p>
          <LineReveal className="mt-4 font-display text-4xl font-black leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl" lines={data.headline} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">{data.intro}</p>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.25em] text-white/50" data-testid="local-byline">
              From Victory Chapel Clay &middot; located in Middleburg, FL &middot; Launching January 2027
            </p>
          </Reveal>
          <Reveal delay={0.5}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/im-new" onClick={trackPlanVisitClick} data-testid="local-plan-visit-cta"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-victory px-8 py-4 font-display text-base font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                PLAN YOUR VISIT <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" onClick={trackDirectionsClick} data-testid="local-directions-cta"
                 className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-8 py-4 font-display text-base font-700 text-white transition-colors duration-300 hover:bg-white/10">
                GET DIRECTIONS <MapPin size={18} aria-hidden="true" />
              </a>
              <Link to="/prayer" data-testid="local-prayer-cta"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-8 py-4 font-display text-base font-700 text-white transition-colors duration-300 hover:bg-white/10">
                REQUEST PRAYER
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-14 sm:py-20" data-testid="local-glance-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-3xl bg-navy p-7 text-white shadow-lift sm:p-10">
              <p className="font-display text-xs font-700 uppercase tracking-[0.3em] text-rose-300">At a glance</p>
              <h2 className="mt-3 text-balance font-display text-2xl font-800 tracking-tight sm:text-3xl">
                LAUNCHING JANUARY 2027 - BE THERE FROM THE BEGINNING.
              </h2>
              <Stagger className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {GLANCE.map((g) => (
                  <StaggerItem key={g.label}>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                      <g.icon size={20} className="text-victory" aria-hidden="true" />
                      <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/50">{g.label}</p>
                      <p className="mt-1 text-sm font-semibold text-white">{g.value}</p>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24" data-testid="local-why-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 sm:gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-balance font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">
              WHY {data.city.toUpperCase()} FAMILIES ARE ALREADY CONNECTING WITH VC CLAY.
            </h2>
            {data.why.map((p, i) => (
              <p key={i} className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">{p}</p>
            ))}
            <p className="mt-6 text-sm text-slate-500">
              Ready to come see us? <Link to={`/${data.legacySlug}`} data-testid="local-legacy-link" className="font-semibold text-victory transition-colors hover:text-victory-dark">Plan Your Visit from {data.city}</Link> - we'll be watching for you at the door.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-3xl bg-cream p-7 sm:p-10">
              <h3 className="font-display text-xl font-800 tracking-tight text-navy">GETTING HERE FROM {data.city.toUpperCase()}</h3>
              <p className="mt-4 leading-relaxed text-slate-600">{data.drive}</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                <li className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" /> {formatAddress(s) || AREA_DESCRIPTOR}</li>
                <li className="flex gap-3"><Clock size={16} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" /> Launching January 2027 - then Sundays 11:00 AM &amp; Wednesdays 7:00 PM</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-24" data-testid="local-life-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="font-display text-sm font-700 uppercase tracking-[0.3em] text-victory">What we're building</p>
            <h2 className="mt-3 text-balance font-display text-3xl font-black tracking-tighter text-navy sm:text-5xl">
              WORSHIP. THE WORD. FAMILY.
            </h2>
          </Reveal>
          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2">
            {[
              { icon: Music, title: "Worship", text: data.worship },
              { icon: BookOpen, title: "Bible Preaching", text: data.preaching },
              { icon: Baby, title: "Children's Ministry", text: data.kids },
              { icon: Users, title: "Community & Discipleship", text: data.community },
            ].map((c) => (
              <StaggerItem key={c.title}>
                <div className="h-full rounded-3xl border border-navy/10 bg-white p-7" data-testid={`local-life-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-white">
                    <c.icon size={22} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-800 tracking-tight text-navy">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-3xl bg-navy p-7 text-white sm:p-9" data-testid="local-expect-card">
              <h3 className="font-display text-xl font-800 tracking-tight">WHAT TO EXPECT ON A FIRST VISIT</h3>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/75 sm:text-base">{data.expect}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-testid="local-faq-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">
              QUESTIONS FROM {data.city.toUpperCase()} FAMILIES
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <Accordion type="single" collapsible className="mt-8" data-testid="local-faq-accordion">
              {data.faqs.map((f, i) => (
                <AccordionItem key={i} value={`clf-${i}`} className="border-navy/10">
                  <AccordionTrigger data-testid={`local-faq-trigger-${i}`} className="text-left font-display text-lg font-700 text-navy hover:text-victory">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy py-14 text-white sm:py-16" data-testid="local-cta-section">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <h2 className="text-balance font-display text-3xl font-black tracking-tighter sm:text-4xl">
              PLAN YOUR FIRST VISIT FROM {data.city.toUpperCase()}.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/75">
              Thirty seconds now, and we'll be watching for you when doors open in January 2027.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3.5">
              <Link to="/im-new" onClick={trackPlanVisitClick} data-testid="local-bottom-visit-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-victory px-7 py-3.5 font-display text-sm font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                PLAN YOUR VISIT <ArrowRight size={15} aria-hidden="true" />
              </Link>
              <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" data-testid="local-bottom-directions-cta"
                 className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-700 text-white transition-colors duration-300 hover:bg-white/10">
                GET DIRECTIONS <MapPin size={15} aria-hidden="true" />
              </a>
              <Link to="/prayer" data-testid="local-bottom-prayer-cta"
                    className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-700 text-white transition-colors duration-300 hover:bg-white/10">
                REQUEST PRAYER
              </Link>
            </div>
            <p className="mt-6 text-sm text-white/50">
              Exploring the area? <Link to="/churches-in-clay-county-fl" data-testid="local-hub-link" className="font-semibold text-rose-300 transition-colors hover:text-white">See churches across Clay County</Link>
            </p>
            <p className="mt-3 text-sm text-white/50" data-testid="local-sibling-links">
              Also convenient for families in:{" "}
              {CHURCH_LOCAL_LIST.filter((c) => c.slug !== data.slug).map((c, i, arr) => (
                <span key={c.slug}>
                  <Link to={`/${c.slug}`} className="font-semibold text-white/70 transition-colors hover:text-white">{c.city}</Link>
                  {i < arr.length - 1 ? " · " : ""}
                </span>
              ))}
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
